'use client';

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

/**
 * Setup deep link handlers for Capacitor mobile apps
 * Handles:
 * - OAuth callbacks (Google, etc.)
 * - Listing deep links
 * - General app navigation
 */
export async function setupDeepLinks() {
  // Only run on native platforms (iOS/Android)
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  console.log('[Capacitor] Setting up deep link handlers...');

  // Listen for app URL open events (deep links, universal links)
  CapacitorApp.addListener('appUrlOpen', (event) => {
    console.log('[Capacitor] App URL opened:', event.url);

    try {
      const url = new URL(event.url);

      // Handle OAuth callbacks (e.g., com.usland.app://oauth/google)
      if (url.protocol === 'com.usland.app:' && url.host === 'oauth') {
        const provider = url.pathname.substring(1); // Remove leading /
        const searchParams = url.search;

        console.log('[Capacitor] OAuth callback detected:', provider);

        // Redirect to NextAuth callback URL
        const callbackUrl = `/api/auth/callback/${provider}${searchParams}`;
        window.location.href = callbackUrl;
        return;
      }

      // Handle HTTPS deep links (usland.com scheme)
      if (url.protocol === 'https:' && url.hostname === 'usland.com') {
        // Navigate to the path directly
        window.location.href = url.pathname + url.search;
        return;
      }

      // Handle listing deep links (e.g., usland://listing/123)
      if (url.protocol === 'usland:' && url.pathname.startsWith('/listing/')) {
        window.location.href = url.pathname + url.search;
        return;
      }

      // Handle custom app scheme deep links (com.usland.app://...)
      if (url.protocol === 'com.usland.app:') {
        // Navigate to the path
        window.location.href = url.pathname + url.search;
        return;
      }

      // Default: navigate to pathname if available
      if (url.pathname && url.pathname !== '/') {
        window.location.href = url.pathname + url.search;
      }

    } catch (error) {
      console.error('[Capacitor] Error handling deep link:', error);
    }
  });

  // Listen for app state changes (foreground/background)
  CapacitorApp.addListener('appStateChange', (state) => {
    console.log('[Capacitor] App state changed:', state.isActive ? 'active' : 'background');

    // Optionally refresh session when app comes to foreground
    if (state.isActive) {
      // You can add session refresh logic here if needed
    }
  });

  console.log('[Capacitor] Deep link handlers registered successfully');
}

/**
 * Check if running on native Capacitor platform
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get the native platform (ios or android)
 */
export function getPlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}
